/**
 * KAN-898: кнопки "В мусор" (с подтверждением) и "Вернуть" (без подтверждения)
 * для контакта. Общие для карточки кабинета, блока "Итог" приложения менеджера
 * и строки таблицы CRM. Кнопка не рисуется, если запрос закрыт пользователю
 * (useCanCallApi: менеджеру по api_access, "Вернуть" только владельцу).
 * Запрос, тост и обновление списков в useClientJunk; onDone - что сделать после
 * успеха (уйти к списку, назад).
 * ClientJunkControls - для карточки: контакт в мусоре (junk_at) - метка "В мусоре"
 * и "Вернуть" (по праву), иначе "В мусор". ClientJunkRowButton - для строки CRM:
 * "В мусор" скрыт у клиента чужого менеджера (то же правило, что у правок строки).
 */
import { useState } from 'react';
import { RotateCcw, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ConfirmDialog, type ConfirmView } from '@/components/telegram/ConfirmDialog';
import { useCanCallApi } from '@/hooks/useApiAccess';
import { useClientManagerRights } from '@/hooks/useClientManagerRights';
import { clientJunkAccessUrl, useClientJunk } from '@/hooks/useClientJunk';
import { cn } from '@/lib/utils';

const JUNK_LABEL = 'В мусор';
const UNJUNK_LABEL = 'Вернуть';
const JUNK_BADGE_LABEL = 'В мусоре';

const JUNK_CONFIRM: ConfirmView = {
  title: JUNK_LABEL,
  description: 'Убрать контакт в мусор? Он пропадёт из всех списков, данные сохранятся.',
  confirm: JUNK_LABEL,
};

interface ClientJunkButtonProps {
  clientId: string;
  /** После успешного запроса (переход к списку, назад). */
  onDone?: (clientId: string) => void;
  /** Только иконка (подпись в title и aria-label): колонка действий таблицы. */
  compact?: boolean;
  className?: string;
  testId?: string;
}

export function ClientJunkButton({
  clientId,
  onDone,
  compact = false,
  className,
  testId = 'client-junk',
}: ClientJunkButtonProps): JSX.Element | null {
  const allowed = useCanCallApi('POST', clientJunkAccessUrl('junk'));
  const [open, setOpen] = useState(false);
  const junk = useClientJunk('junk', onDone);
  if (!allowed) return null;

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="outline"
        data-testid={testId}
        disabled={junk.isPending}
        title={compact ? JUNK_LABEL : undefined}
        aria-label={compact ? JUNK_LABEL : undefined}
        className={cn(compact ? 'h-7 w-7 p-0' : 'text-gray-700', className)}
        onClick={() => setOpen(true)}
      >
        <Trash2 className={compact ? 'h-3.5 w-3.5' : 'mr-1 h-3.5 w-3.5'} />
        {!compact && JUNK_LABEL}
      </Button>
      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        view={JUNK_CONFIRM}
        onConfirm={() => junk.mutate(clientId)}
        testId={`${testId}-confirm`}
      />
    </>
  );
}

export function ClientUnjunkButton({
  clientId,
  onDone,
  className,
  testId = 'client-unjunk',
}: Omit<ClientJunkButtonProps, 'compact'>): JSX.Element | null {
  const allowed = useCanCallApi('POST', clientJunkAccessUrl('unjunk'));
  const unjunk = useClientJunk('unjunk', onDone);
  if (!allowed) return null;

  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      data-testid={testId}
      disabled={unjunk.isPending}
      className={cn('h-7 px-2', className)}
      onClick={() => unjunk.mutate(clientId)}
    >
      <RotateCcw className="mr-1 h-3.5 w-3.5" />
      {UNJUNK_LABEL}
    </Button>
  );
}

interface ClientJunkRowButtonProps {
  clientId: string;
  /** Менеджер клиента строки: у чужого клиента обычному менеджеру кнопка не нужна (403). */
  managerId: string | null | undefined;
  testId?: string;
}

export function ClientJunkRowButton({ clientId, managerId, testId }: ClientJunkRowButtonProps): JSX.Element | null {
  const { canChange } = useClientManagerRights(managerId);
  if (!canChange) return null;
  return <ClientJunkButton clientId={clientId} compact testId={testId} />;
}

interface ClientJunkControlsProps {
  clientId: string;
  /** Когда контакт убран в мусор (ISO); null/нет - не в мусоре. */
  junkAt: string | null | undefined;
  /** После "В мусор" (уйти к списку, назад). После "Вернуть" карточка остаётся. */
  onJunked?: (clientId: string) => void;
  /** Классы кнопки "В мусор". */
  junkClassName?: string;
  className?: string;
}

export function ClientJunkControls({
  clientId,
  junkAt,
  onJunked,
  junkClassName,
  className,
}: ClientJunkControlsProps): JSX.Element {
  if (!junkAt) {
    return <ClientJunkButton clientId={clientId} onDone={onJunked} className={junkClassName} />;
  }
  return (
    <div className={cn('flex items-center gap-2', className)}>
      <span
        data-testid="client-junk-badge"
        className="inline-flex items-center rounded-md border border-gray-300 bg-gray-100 px-2 py-1 text-sm text-gray-700"
      >
        <Trash2 className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
        {JUNK_BADGE_LABEL}
      </span>
      <ClientUnjunkButton clientId={clientId} />
    </div>
  );
}
